import type { Inspection } from "../../../api/dto/Inspection";
import type { Question } from "../../../api/dto/Question";
import { GaugeValue } from "./GaugeValue";

export class WorkstationInspectionCalculator {
  constructor(
    private readonly previousWindow: Inspection[],
    private readonly currentWindow: Inspection[],
  ) {}

  public getHeadTiles(): HeadTiles {
    const currentInspectionsCount = this.currentWindow.length;
    const previousInspectionsCount = this.previousWindow.length;

    const currentAverageInspectionScore =
      this.currentWindow.reduce((c, i) => c + (i?.cachedResults?.score ?? 0), 0) /
      Math.max(1, this.currentWindow.length);
    const previousAverageInspectionScore =
      this.previousWindow.reduce((c, i) => c + (i?.cachedResults?.score ?? 0), 0) /
      Math.max(1, this.previousWindow.length);

    const currentOpenFindings = this.currentWindow.reduce(
      (c, i) => c + (i?.cachedResults?.critical_findings_open ?? 0),
      0,
    );
    const previousOpenFindings = this.previousWindow.reduce(
      (c, i) => c + (i?.cachedResults?.critical_findings_open ?? 0),
      0,
    );

    const currentLsrFindings = this.currentWindow.reduce(
      (c, i) => c + (i?.cachedResults?.lsr_findings_total ?? 0),
      0,
    );
    const previousLsrFindings = this.previousWindow.reduce(
      (c, i) => c + (i?.cachedResults?.lsr_findings_total ?? 0),
      0,
    );

    const currentSolveRate =
      (this.currentWindow.reduce(
        (c, i) => c + (i?.cachedResults?.critical_findings_fixed ?? 0),
        0,
      ) /
        Math.max(
          1,
          this.currentWindow.reduce(
            (c, i) => c + (i?.cachedResults?.critical_findings_total ?? 0),
            0,
          ),
        )) *
      100;
    const previousSolveRate =
      (this.previousWindow.reduce(
        (c, i) => c + (i?.cachedResults?.critical_findings_fixed ?? 0),
        0,
      ) /
        Math.max(
          1,
          this.previousWindow.reduce(
            (c, i) => c + (i?.cachedResults?.critical_findings_total ?? 0),
            0,
          ),
        )) *
      100;

    return {
      inspectionsCount: new GaugeValue(previousInspectionsCount, currentInspectionsCount),
      averageInspectionScore: new GaugeValue(
        previousAverageInspectionScore,
        currentAverageInspectionScore,
        "",
        1,
      ),
      lsrFindings: new GaugeValue(previousLsrFindings, currentLsrFindings),
      openFindings: new GaugeValue(previousOpenFindings, currentOpenFindings),
      solveRate: new GaugeValue(previousSolveRate, currentSolveRate, "%"),
    };
  }

  public getCategoryScoreTiles(): CategoryTiles {
    return {
      knowledge: this.getCategoryScoreGauge("Basis Kennis"),
      expertise: this.getCategoryScoreGauge("Expertise"),
      behavior: this.getCategoryScoreGauge("Gedrag"),
      safety: this.getCategoryScoreGauge("Veiligheid"),
    };
  }

  public getCriticalFindingsTrend(): CriticalFindingTrend {
    const currentQuestionIdCountsForCriticalFindings =
      this.countQuestionIdsForCriticalOrLSRFindings(this.currentWindow);
    const previousQuestionIdCountsForCriticalFindings =
      this.countQuestionIdsForCriticalOrLSRFindings(this.previousWindow);

    const allQuestionIds = Array.from(
      new Set([
        ...Object.keys(currentQuestionIdCountsForCriticalFindings),
        ...Object.keys(previousQuestionIdCountsForCriticalFindings),
      ]),
    );

    const deltaMap: { [questionId: string]: { delta: number; previous: number; current: number } } =
      Object.fromEntries(
        allQuestionIds.map((id) => {
          return [
            id,
            {
              delta:
                (currentQuestionIdCountsForCriticalFindings[id] ?? 0) -
                (previousQuestionIdCountsForCriticalFindings[id] ?? 0),
              previous: previousQuestionIdCountsForCriticalFindings[id] ?? 0,
              current: currentQuestionIdCountsForCriticalFindings[id] ?? 0,
            },
          ];
        }),
      );

    const risingFindingQuestionIds = Object.entries(deltaMap)
      .sort((a, b) => b[1].delta - a[1].delta)
      .filter((x) => x[1].delta > 0)
      .slice(0, 3)
      .map((x) => ({
        question: x[0],
        deltaPercent:
          x[1].delta / Math.max(1, previousQuestionIdCountsForCriticalFindings[x[0]] ?? 0),
        previousCount: x[1].previous,
        currentCount: x[1].current,
      }));

    const fallingFindingQuestionIds = Object.entries(deltaMap)
      .sort((a, b) => a[1].delta - b[1].delta)
      .filter((x) => x[1].delta < 0)
      .slice(0, 3)
      .map((x) => ({
        question: x[0],
        deltaPercent:
          x[1].delta / Math.max(1, previousQuestionIdCountsForCriticalFindings[x[0]] ?? 0),
        previousCount: x[1].previous,
        currentCount: x[1].current,
      }));

    return {
      risingFindingQuestionIds,
      fallingFindingQuestionIds,
    };
  }

  public getTopPositiveQuestions(allQuestions: Question[]): {
    question: Question["id"];
    previousCount: number;
    currentCount: number;
    delta: number;
    deltaPercent: number;
  }[] {
    const currentCounts = this.countQuestionIdsForCriticalOrLSRFindings(this.currentWindow);
    const previousCounts = this.countQuestionIdsForCriticalOrLSRFindings(this.previousWindow);
    return allQuestions
      .map((q) => ({
        question: q.id,
        currentCount: currentCounts[q.id] ?? 0,
        previousCount: previousCounts[q.id] ?? 0,
      }))
      .sort((a, b) => a.currentCount - b.currentCount || b.previousCount - a.previousCount)
      .slice(0, 3)
      .map((x) => ({
        ...x,
        delta: x.currentCount - x.previousCount,
        deltaPercent: (x.currentCount - x.previousCount) / Math.max(1, x.previousCount),
      }));
  }

  private countQuestionIdsForCriticalOrLSRFindings(inspections: Inspection[]): {
    [questionId: string]: number;
  } {
    return inspections
      .flatMap((inspection) =>
        Array.from(
          new Set([
            ...(inspection?.cachedResults?.critical_finding_question_ids ?? []),
            ...(inspection?.cachedResults?.lsr_finding_question_ids ?? []),
          ]),
        ),
      )
      .reduce(
        (countMap, questionId) => {
          if (countMap[questionId] === undefined) {
            countMap[questionId] = 0;
          }
          countMap[questionId] += 1;
          return countMap;
        },
        {} as { [questionId: string]: number },
      );
  }

  private getCategoryScoreGauge(category: string) {
    const currentScore =
      (this.currentWindow.reduce(
        (c, i) => c + (i?.cachedResults?.compliance_percentage_per_category[category] ?? 0),
        0,
      ) /
        Math.max(1, this.currentWindow.length)) *
      100;
    const previousScore =
      (this.previousWindow.reduce(
        (c, i) => c + (i?.cachedResults?.compliance_percentage_per_category[category] ?? 0),
        0,
      ) /
        Math.max(1, this.previousWindow.length)) *
      100;
    return new GaugeValue(previousScore, currentScore, "%", 0);
  }
}

interface HeadTiles {
  inspectionsCount: GaugeValue;
  averageInspectionScore: GaugeValue;
  lsrFindings: GaugeValue;
  openFindings: GaugeValue;
  solveRate: GaugeValue;
}
interface CategoryTiles {
  knowledge: GaugeValue;
  expertise: GaugeValue;
  behavior: GaugeValue;
  safety: GaugeValue;
}
interface CriticalFindingTrend {
  risingFindingQuestionIds: {
    question: Question["id"];
    deltaPercent: number;
    previousCount: number;
    currentCount: number;
  }[];
  fallingFindingQuestionIds: {
    question: Question["id"];
    deltaPercent: number;
    previousCount: number;
    currentCount: number;
  }[];
}
